import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageSquare, ArrowUp, Award, CheckCheck, Megaphone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { fetchNotifications, fetchUnreadCount, markNotificationsRead } from "@/lib/api";
import { onMenu } from "@/lib/menuBus";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ICONS = { reply: MessageSquare, upvote: ArrowUp, best: Award, reminder: Bell, announcement: Megaphone };

function timeAgo(iso, en) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return en ? "just now" : "अभी";
  const m = Math.floor(s / 60);
  if (m < 60) return en ? `${m}m ago` : `${m} मि.`;
  const h = Math.floor(m / 60);
  if (h < 24) return en ? `${h}h ago` : `${h} घं.`;
  const d = Math.floor(h / 24);
  return en ? `${d}d ago` : `${d} दिन`;
}

function notifText(n, en, sm = {}) {
  if (n.type === "reminder" || n.type === "announcement") return n.text || n.title || "";
  const who = n.actor_name || (en ? "A sadhak" : "एक साधक");
  const more = n.count > 1 ? (en ? ` and ${n.count - 1} other${n.count - 1 > 1 ? "s" : ""}` : ` व ${n.count - 1} अन्य`) : "";
  const fill = (tpl, what) => (tpl || "").replace("{who}", who).replace("{more}", more).replace("{what}", what || "");
  if (n.type === "reply") {
    const what = n.kind === "qna" ? (en ? "question" : "प्रश्न") : (en ? "post" : "विषय");
    const tpl = en ? sm.notif_reply?.en : sm.notif_reply?.hi;
    return tpl ? fill(tpl, what) : (en ? `${who} answered your ${what}` : `${who} ने आपके ${what} का उत्तर दिया`);
  }
  if (n.type === "best") {
    const tpl = en ? sm.notif_best?.en : sm.notif_best?.hi;
    return tpl ? fill(tpl) : (en ? `${who} marked your answer as the best answer` : `${who} ने आपके उत्तर को सर्वोत्तम चुना`);
  }
  if (n.type === "upvote") {
    const what = n.target === "reply" ? (en ? "answer" : "उत्तर") : (en ? "post" : "विषय");
    const tpl = en ? sm.notif_upvote?.en : sm.notif_upvote?.hi;
    return tpl ? fill(tpl, what) : (en ? `${who}${more} upvoted your ${what}` : `${who}${more} ने आपके ${what} को सराहा`);
  }
  return "";
}

export default function NotificationBell() {
  const { pick } = useLang();
  const { system_messages } = useSettings();
  const en = pick("", "x") === "x";
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      // Derive the badge from the real list so it can never show a phantom
      // count with an empty panel.
      const r = await fetchNotifications();
      const list = r.notifications || [];
      setItems(list);
      setUnread(typeof r.unread === "number" ? Math.min(r.unread, list.filter((n) => !n.read).length || r.unread) : list.filter((n) => !n.read).length);
      if (list.length === 0) setUnread(0);
    } catch { /* logged out / ignore */ }
  }, []);

  useEffect(() => {
    loadCount();
    timer.current = setInterval(loadCount, 60000);
    return () => clearInterval(timer.current);
  }, [loadCount]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchNotifications();
      setItems(r.notifications || []);
      setUnread(r.unread || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) loadList();
    // Safety net: a modal Radix dropdown locks `pointer-events` on <body> while
    // open; if a navigation interrupts its close cleanup the whole page appears
    // frozen. Explicitly clear the lock whenever the menu closes.
    if (!v && typeof document !== "undefined") {
      requestAnimationFrame(() => { document.body.style.pointerEvents = ""; });
    }
  };

  // Never leave the page unclickable if this component unmounts while open
  // (e.g. after navigating from a notification click).
  useEffect(() => () => { if (typeof document !== "undefined") document.body.style.pointerEvents = ""; }, []);

  // Rule 1: the notification dropdown must never sit over the open menu.
  useEffect(() => onMenu((o) => { if (o) setOpen(false); }), []);

  const openNotif = async (n) => {
    setOpen(false);
    try {
      await markNotificationsRead(n.id);
      setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
    } catch { /* ignore */ }
    if (n.link) { navigate(n.link); return; }
    const base = n.kind === "qna" ? "/qna" : "/discussions";
    navigate(`${base}/${n.thread_id}`);
  };

  const markAll = async () => {
    try {
      await markNotificationsRead();
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
      <DropdownMenuTrigger data-testid="notif-bell" aria-label="Notifications" className="relative text-ink-2 transition-colors hover:text-saffron focus:outline-none">
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-saffron px-1 text-[10px] font-semibold leading-none text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={12} className="w-[20rem] rounded-none border-line bg-surface p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <p className="overline-label text-ink-3">{en ? "Notifications" : "सूचनाएँ"}</p>
          {items.some((n) => !n.read) && (
            <button data-testid="notif-mark-all" onClick={markAll} className="inline-flex items-center gap-1 text-[11px] text-saffron hover:underline">
              <CheckCheck className="h-3.5 w-3.5" /> {en ? "Mark all read" : "सभी पढ़ी"}
            </button>
          )}
        </div>
        <div className="max-h-[22rem] overflow-y-auto">
          {loading ? (
            <p className="px-4 py-8 text-center text-sm text-ink-3">…</p>
          ) : items.length === 0 ? (
            <p data-testid="notif-empty" className="px-4 py-10 text-center text-sm text-ink-3">
              {en ? "No notifications yet." : "अभी कोई सूचना नहीं।"}
            </p>
          ) : (
            <ul className="divide-y divide-line/60">
              {items.map((n) => {
                const Icon = ICONS[n.type] || Bell;
                return (
                  <li key={n.id}>
                    <button
                      onClick={() => openNotif(n)}
                      data-testid={`notif-item-${n.id}`}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-alt ${n.read ? "" : "bg-[rgba(199,80,0,0.05)]"}`}
                    >
                      <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${n.read ? "bg-surface-alt text-ink-3" : "bg-saffron/15 text-saffron"}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] leading-snug text-ink">{notifText(n, en, system_messages || {})}</span>
                        <span className="mt-0.5 block truncate text-[11px] text-ink-3">
                          {n.thread_title ? `${n.thread_title} · ` : ""}{timeAgo(n.updated_at || n.created_at, en)}
                        </span>
                      </span>
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-saffron" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
